import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import { purchaseOrderImportService } from '../services'
import ImportReviewTableRow from '../components/purchase-order-import/ImportReviewTableRow'
import ResponsiveLayout from '../components/create-purchase-order/ResponsiveLayout'
import OrderSummary from '../components/create-purchase-order/OrderSummary'

const buildLotOptions = (line) => {
  const options = [...(line.candidate_lots || [])]
  if (
    line.inventory_id &&
    !options.some((o) => o.inventory_id === line.inventory_id)
  ) {
    options.unshift({
      inventory_id: line.inventory_id,
      location_name: line.location_name || 'Selected lot',
      available_quantity: line.available_quantity,
      unit_price: line.unit_price,
      unit_cost: line.unit_cost,
      delivery_estimate: line.delivery_estimate,
    })
  }
  return options
}

const PurchaseOrderImportReviewPage = () => {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const [session, setSession] = useState(null)
  const [lines, setLines] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const [patching, setPatching] = useState({})

  const fetchSession = useCallback(async () => {
    const data = await purchaseOrderImportService.getSession(sessionId)
    setSession(data)
    return data
  }, [sessionId])

  const fetchLines = useCallback(async () => {
    setLoading(true)
    try {
      const data = await purchaseOrderImportService.getLines(sessionId, {
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter || undefined,
      })
      setLines(data.items || [])
      setTotalCount(data.pagination?.total || 0)
      setError(null)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load lines')
    } finally {
      setLoading(false)
    }
  }, [sessionId, page, rowsPerPage, statusFilter])

  useEffect(() => {
    fetchSession().catch(() => {})
    fetchLines()
  }, [fetchSession, fetchLines])

  const duplicateSkuWarning = useMemo(() => {
    const bySku = {}
    for (const line of lines) {
      if (line.is_excluded) continue
      const key = (line.article_code || '').toUpperCase()
      if (!key) continue
      bySku[key] = bySku[key] || new Set()
      if (line.inventory_id) bySku[key].add(line.inventory_id)
    }
    const conflicts = Object.entries(bySku).filter(([, lots]) => lots.size > 1)
    if (!conflicts.length) return null
    return `Some article codes appear on multiple lots (${conflicts.map(([s]) => s).join(', ')}). Quantities will only merge when the same lot is selected.`
  }, [lines])

  const outsideMappedCount = useMemo(
    () =>
      lines.filter(
        (l) =>
          !l.is_excluded &&
          (l.outside_mapped_location ||
            (l.warnings || []).some((w) =>
              String(w).toLowerCase().includes('outside'),
            )),
      ).length,
    [lines],
  );

  const handleLineUpdated = useCallback(
    async (updatedLine) => {
      if (updatedLine?.id) {
        setLines((prev) =>
          prev.map((l) => (l.id === updatedLine.id ? updatedLine : l)),
        );
      }
      await fetchSession();
    },
    [fetchSession],
  );

  const cartStats = useMemo(() => {
    const active = lines.filter(
      (l) =>
        !l.is_excluded &&
        l.inventory_id &&
        (l.status === 'resolved' || l.status === 'warning') &&
        l.requested_quantity >= 1,
    )
    const lineCount = active.reduce((s, l) => s + l.requested_quantity, 0)
    const estimatedSubtotal = active.reduce(
      (s, l) =>
        s +
        (Number(
          l.effective_unit_cost ??
            l.override_unit_cost ??
            l.unit_cost ??
            l.unit_price,
        ) || 0) * l.requested_quantity,
      0,
    )
    const hasInsufficientStockWarning = active.some((l) => l.status === 'warning')
    const hasOosBackorder = active.some(
      (l) => (Number(l.available_quantity) || 0) < 1,
    )
    return {
      lineCount,
      estimatedSubtotal,
      hasInsufficientStockWarning,
      hasOosBackorder,
      activeCount: active.length,
    }
  }, [lines])

  const patchLineRemote = async (lineId, body) => {
    setPatching((p) => ({ ...p, [lineId]: true }))
    try {
      const updated = await purchaseOrderImportService.patchLine(
        sessionId,
        lineId,
        body,
      )
      setLines((prev) => prev.map((l) => (l.id === lineId ? updated : l)))
      await fetchSession()
    } catch (err) {
      const msg = err.response?.data?.message
      setSubmitError(Array.isArray(msg) ? msg.join(', ') : msg || 'Update failed')
    } finally {
      setPatching((p) => ({ ...p, [lineId]: false }))
    }
  }

  const handleQtyChange = (lineId, value, opts = {}) => {
    if (opts.localOnly) {
      setLines((prev) =>
        prev.map((l) =>
          l.id === lineId ? { ...l, requested_quantity: value } : l,
        ),
      )
      return
    }
    const qty = Math.floor(Number(value))
    if (!Number.isFinite(qty) || qty < 1) return
    patchLineRemote(lineId, { requested_quantity: qty })
  }

  const handleLotChange = (lineId, inventoryId) => {
    patchLineRemote(lineId, { inventory_id: inventoryId })
  }

  const handleExcludeChange = (lineId, isExcluded) => {
    patchLineRemote(lineId, { is_excluded: isExcluded })
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const res = await purchaseOrderImportService.submit(sessionId)
      if (res.warnings?.length) {
        console.warn('PO import submit warnings:', res.warnings)
      }
      const orderId = res.order_id
      if (orderId) {
        navigate(`/orders/${orderId}`, {
          state: { ordersListPath: '/orders/purchase' },
        })
      }
    } catch (err) {
      const msg = err.response?.data?.message
      setSubmitError(
        Array.isArray(msg) ? msg.join(', ') : msg || 'Failed to create order',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleExportErrors = async () => {
    try {
      const blob = await purchaseOrderImportService.exportErrors(sessionId)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `po-import-errors-${sessionId}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      setSubmitError('Failed to export errors')
    }
  }

  const summaryPanel = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        position: { md: 'sticky' },
        top: { md: 88 },
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Order summary
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {cartStats.activeCount} line(s) on this page included
      </Typography>
      {cartStats.hasInsufficientStockWarning && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Some lines exceed available stock and will be ordered as backorder.
        </Alert>
      )}
      {outsideMappedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {outsideMappedCount} line(s) on this page use inventory outside designated
          warehouses.
        </Alert>
      )}
      {cartStats.hasOosBackorder && !cartStats.hasInsufficientStockWarning && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Some lots are out of stock and will be ordered as backorder.
        </Alert>
      )}
      <OrderSummary
        lineCount={session?.resolved_rows ?? cartStats.lineCount}
        estimatedSubtotal={cartStats.estimatedSubtotal}
        onSubmit={handleSubmit}
        submitting={submitting}
        disabled={
          session?.status !== 'ready_for_review' ||
          (session?.resolved_rows ?? 0) < 1
        }
      />
    </Paper>
  )

  const tableContent = (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status filter</InputLabel>
          <Select
            label="Status filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(0)
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="warning">Warnings</MenuItem>
            <MenuItem value="error">Errors</MenuItem>
          </Select>
        </FormControl>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExportErrors}
        >
          Export errors
        </Button>
      </Box>

      {duplicateSkuWarning && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {duplicateSkuWarning}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Article code</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Lot</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Delivery</TableCell>
                  <TableCell align="right">Available</TableCell>
                  <TableCell align="right">Selling Price</TableCell>
                  <TableCell align="right">MRP</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                  <TableCell>Include</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line) => (
                  <ImportReviewTableRow
                    key={line.id}
                    line={line}
                    lotOptions={buildLotOptions(line)}
                    orgId={session?.org_id}
                    sessionId={sessionId}
                    onQtyChange={handleQtyChange}
                    onLotChange={handleLotChange}
                    onExcludeChange={handleExcludeChange}
                    onLineUpdated={handleLineUpdated}
                    onActionError={setSubmitError}
                    disabled={Boolean(patching[line.id])}
                  />
                ))}
                {!lines.length && (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      No lines match this filter
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
          />
        </>
      )}
    </Box>
  )

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/orders/purchase/create/upload/${sessionId}`)}
        sx={{ mb: 2, color: '#666' }}
      >
        Back to progress
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Review import
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Confirm quantities and warehouse lots, then create the purchase order.
        {session?.org_id ? ` Shop Id #${session.org_id}` : ''}
      </Typography>

      <ResponsiveLayout catalog={tableContent} cart={summaryPanel} />
    </Box>
  )
}

export default PurchaseOrderImportReviewPage
